import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, ShieldCheck, Landmark, Globe2 } from "lucide-react";
import { MaskedLine, FadeUp, ParallaxImage } from "@/components/Reveal";
import { Marquee } from "@/components/Marquee";
import { Faq } from "@/components/Faq";
import { IMAGES, usePageTitle } from "@/lib/site";

const EASE = [0.16, 1, 0.3, 1];

const SERVICES = [
  {
    num: "01",
    title: "Accounting",
    desc: "Full-charge accounting, monthly closes, financial statements, and grant & fund accounting built for how your organization actually runs.",
    to: "/services#accounting",
    testId: "home-service-card-accounting",
    span: "lg:col-span-7",
  },
  {
    num: "02",
    title: "Audit Preparation",
    desc: "Audit-ready workpapers, reconciliations, and PBC coordination so fieldwork feels routine — including Uniform Guidance single audits for tribal governments.",
    to: "/services#audit-preparation",
    testId: "home-service-card-audit",
    span: "lg:col-span-5",
  },
  {
    num: "03",
    title: "Bookkeeping",
    desc: "Dependable monthly bookkeeping, AP/AR, bank reconciliations, and clean-up work that keeps every ledger current and every report trustworthy.",
    to: "/services#bookkeeping",
    testId: "home-service-card-bookkeeping",
    span: "lg:col-span-12",
  },
];

const PILLARS = [
  {
    num: "01",
    title: "Precision",
    desc: "Every reconciliation ties out. Every report is one you would hand to an auditor without a second thought.",
  },
  {
    num: "02",
    title: "Transparency",
    desc: "Plain-language reporting and proactive communication. You always know where your numbers stand — and why.",
  },
  {
    num: "03",
    title: "Partnership",
    desc: "We work as an extension of your team, on a cadence that fits your fiscal year, your board, and your community.",
  },
];

export default function Home() {
  usePageTitle("CalCor Solutions — Accounting, Audit Prep & Bookkeeping | Seattle & Remote");

  return (
    <>
      {/* ————— Kinetic hero ————— */}
      <section data-testid="home-hero" className="relative overflow-hidden pt-36 pb-20 sm:pt-44 lg:pb-28">
        <div className="mx-auto grid max-w-7xl items-center gap-16 px-6 lg:grid-cols-12 lg:px-10">
          <div className="lg:col-span-7">
            <motion.p
              data-testid="hero-eyebrow"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.15 }}
              className="flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.25em] text-pine"
            >
              <span className="h-px w-10 bg-pine" />
              Accounting · Audit Preparation · Bookkeeping
            </motion.p>

            <h1 className="mt-8 font-serif text-5xl font-medium leading-[1.02] tracking-tighter text-ink sm:text-6xl lg:text-[4.6rem] xl:text-[5.2rem]">
              <MaskedLine delay={0.25}>Clear books.</MaskedLine>
              <MaskedLine delay={0.4}>
                <em className="text-pine">Confident audits.</em>
              </MaskedLine>
              <MaskedLine delay={0.55}>Calm growth.</MaskedLine>
            </h1>

            <motion.p
              data-testid="hero-subheading"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.75, ease: EASE }}
              className="mt-8 max-w-xl text-base leading-relaxed text-zinc-600 sm:text-lg"
            >
              CalCor Solutions keeps small businesses and tribal governments financially
              confident — from our home in Seattle to clients everywhere, fully remote-capable.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.9, ease: EASE }}
              className="mt-10 flex flex-wrap items-center gap-4"
            >
              <Link
                to="/contact#booking"
                data-testid="hero-book-consultation-button"
                className="group inline-flex items-center gap-2 rounded-full bg-pine px-7 py-4 text-sm font-semibold text-white transition-colors duration-300 hover:bg-clay"
              >
                Book a consultation
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <Link
                to="/services"
                data-testid="hero-explore-services-button"
                className="group inline-flex items-center gap-2 rounded-full border border-black/15 px-7 py-4 text-sm font-semibold text-ink transition-colors duration-300 hover:border-pine hover:text-pine"
              >
                Explore services
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </Link>
            </motion.div>
          </div>

          <motion.div
            className="relative lg:col-span-5"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.1, delay: 0.5, ease: EASE }}
          >
            <div className="absolute -left-4 -top-4 h-full w-full rounded-t-[999px] border border-pine/30" aria-hidden="true" />
            <ParallaxImage
              src={IMAGES.seattle}
              alt="Seattle skyline at twilight"
              arch
              testId="hero-image"
              className="relative aspect-[3/4] w-full"
            />
            <div
              data-testid="hero-stat-card"
              className="absolute -bottom-6 -left-6 rounded-2xl border border-black/10 bg-white px-6 py-5 shadow-sm sm:-left-10"
            >
              <p className="font-serif text-3xl tracking-tight text-pine">100%</p>
              <p className="mt-1 text-xs font-semibold uppercase tracking-[0.18em] text-zinc-500">
                Remote-capable service
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ————— Trust strip ————— */}
      <section data-testid="trust-strip" className="border-y border-black/10 bg-white">
        <div className="mx-auto grid max-w-7xl grid-cols-1 divide-y divide-black/10 px-6 sm:grid-cols-3 sm:divide-x sm:divide-y-0 lg:px-10">
          {[
            { icon: ShieldCheck, label: "Small business specialists" },
            { icon: Landmark, label: "Tribal government experience" },
            { icon: Globe2, label: "Seattle-based · Serving anywhere" },
          ].map(({ icon: Icon, label }, i) => (
            <FadeUp key={label} delay={i * 0.1} className="flex items-center gap-4 px-2 py-7 sm:justify-center">
              <Icon className="h-5 w-5 shrink-0 text-pine" />
              <span className="text-sm font-semibold tracking-wide text-ink">{label}</span>
            </FadeUp>
          ))}
        </div>
      </section>

      {/* ————— Services overview ————— */}
      <section data-testid="services-overview" className="py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <FadeUp>
            <p className="flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.25em] text-pine">
              <span className="h-px w-10 bg-pine" />
              What we do
            </p>
            <h2 className="mt-6 max-w-3xl font-serif text-4xl leading-tight tracking-tighter text-ink sm:text-5xl">
              Three disciplines. <em className="text-pine">One standard of care.</em>
            </h2>
          </FadeUp>

          <div className="mt-16 grid gap-5 lg:grid-cols-12">
            {SERVICES.map((s, i) => (
              <FadeUp key={s.num} delay={i * 0.12} className={s.span}>
                <Link
                  to={s.to}
                  data-testid={s.testId}
                  className="group flex h-full flex-col justify-between rounded-3xl border border-black/10 bg-white p-10 transition-colors duration-300 hover:border-pine"
                >
                  <div>
                    <div className="flex items-start justify-between">
                      <span className="font-serif text-5xl italic text-pine/25 transition-colors duration-300 group-hover:text-pine/50">
                        {s.num}
                      </span>
                      <span className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-black/10 transition-colors duration-300 group-hover:border-pine group-hover:bg-pine">
                        <ArrowUpRight className="h-4 w-4 text-ink transition-colors duration-300 group-hover:text-white" />
                      </span>
                    </div>
                    <h3 className="mt-8 font-serif text-2xl tracking-tight text-ink sm:text-3xl">
                      {s.title}
                    </h3>
                    <p className="mt-4 max-w-lg text-sm leading-relaxed text-zinc-600 sm:text-base">
                      {s.desc}
                    </p>
                  </div>
                </Link>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      <Marquee
        items={[
          "Accounting",
          "Audit Preparation",
          "Bookkeeping",
          "Grant & Fund Accounting",
          "Monthly Closes",
          "Audit-Ready Workpapers",
        ]}
      />

      {/* ————— Manifesto / why ————— */}
      <section data-testid="why-calcor" className="py-24 lg:py-32">
        <div className="mx-auto grid max-w-7xl gap-16 px-6 lg:grid-cols-12 lg:px-10">
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-32">
              <FadeUp>
                <p className="flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.25em] text-pine">
                  <span className="h-px w-10 bg-pine" />
                  Why CalCor
                </p>
                <h2 className="mt-6 font-serif text-4xl leading-tight tracking-tighter text-ink sm:text-5xl">
                  A partner, <em className="text-pine">not a vendor.</em>
                </h2>
                <p className="mt-6 max-w-md text-base leading-relaxed text-zinc-600">
                  Numbers are how organizations keep their promises — to owners, boards,
                  auditors, and communities. We treat that responsibility accordingly.
                </p>
                <Link
                  to="/about"
                  data-testid="why-about-link"
                  className="group mt-8 inline-flex items-center gap-2 text-sm font-semibold text-pine transition-colors hover:text-clay"
                >
                  Our story
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </FadeUp>
            </div>
          </div>

          <div className="lg:col-span-7">
            {PILLARS.map((p, i) => (
              <FadeUp key={p.num} delay={i * 0.08}>
                <div
                  data-testid={`pillar-${p.num}`}
                  className="group flex gap-8 border-t border-black/10 py-12 transition-colors last:border-b hover:bg-white/60"
                >
                  <span className="font-serif text-6xl italic leading-none text-pine/25 transition-colors duration-300 group-hover:text-pine sm:text-7xl">
                    {p.num}
                  </span>
                  <div className="pt-2">
                    <h3 className="font-serif text-2xl tracking-tight text-ink sm:text-3xl">{p.title}</h3>
                    <p className="mt-3 max-w-lg text-sm leading-relaxed text-zinc-600 sm:text-base">
                      {p.desc}
                    </p>
                  </div>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* ————— Remote / Seattle ————— */}
      <section data-testid="remote-section" className="border-y border-black/10 bg-white py-24 lg:py-32">
        <div className="mx-auto grid max-w-7xl items-center gap-16 px-6 lg:grid-cols-12 lg:px-10">
          <div className="lg:col-span-5">
            <ParallaxImage
              src={IMAGES.office}
              alt="Modern minimal workspace"
              testId="remote-image"
              className="aspect-[4/5] w-full rounded-3xl"
            />
          </div>
          <div className="lg:col-span-7 lg:pl-8">
            <FadeUp>
              <p className="flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.25em] text-pine">
                <span className="h-px w-10 bg-pine" />
                Wherever you are
              </p>
              <h2 className="mt-6 font-serif text-4xl leading-tight tracking-tighter text-ink sm:text-5xl">
                Seattle roots. <em className="text-pine">Nationwide reach.</em>
              </h2>
              <p className="mt-6 max-w-xl text-base leading-relaxed text-zinc-600 sm:text-lg">
                Whether you are down the street or across the country, working with CalCor
                feels the same: secure cloud tools, scheduled video check-ins, and documents
                exchanged through a protected client portal. Distance never dilutes the work.
              </p>
              <ul className="mt-8 space-y-4">
                {[
                  "Secure, cloud-based collaboration from day one",
                  "Recurring review calls on your cadence — monthly, quarterly, or audit season",
                  "Same-day responses during filing and audit deadlines",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3 text-sm text-zinc-700 sm:text-base">
                    <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-clay" />
                    {item}
                  </li>
                ))}
              </ul>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* ————— Client stories ————— */}
      <section data-testid="testimonials-section" className="py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <FadeUp>
            <p className="flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.25em] text-pine">
              <span className="h-px w-10 bg-pine" />
              Client stories
            </p>
            <h2 className="mt-6 max-w-3xl font-serif text-4xl leading-tight tracking-tighter text-ink sm:text-5xl">
              Kind words from <em className="text-pine">careful clients.</em>
            </h2>
          </FadeUp>
          <div className="mt-16 grid gap-px overflow-hidden rounded-3xl border border-black/10 bg-black/10 lg:grid-cols-3">
            {[
              {
                quote: "CalCor untangled two years of messy books in a single quarter. Our lender actually complimented the reporting package.",
                by: "Owner, family-run retail group",
                where: "Tacoma, WA",
              },
              {
                quote: "Audit season used to mean panic. Now our workpapers are ready before the auditors even ask.",
                by: "Finance Director, tribal government",
                where: "Pacific Northwest",
              },
              {
                quote: "They answer in plain English, hit every deadline, and our board finally understands the numbers.",
                by: "Executive Director, regional nonprofit",
                where: "Seattle, WA",
              },
            ].map((t, i) => (
              <FadeUp key={t.by} delay={i * 0.1} className="bg-white">
                <figure data-testid={`testimonial-${i + 1}`} className="flex h-full flex-col justify-between p-10 transition-colors duration-300 hover:bg-paper">
                  <blockquote className="font-serif text-xl leading-relaxed tracking-tight text-ink">
                    <span className="mb-4 block font-serif text-5xl italic leading-none text-pine/25">&ldquo;</span>
                    {t.quote}
                  </blockquote>
                  <figcaption className="mt-8">
                    <p className="text-sm font-semibold text-ink">{t.by}</p>
                    <p className="mt-1 text-xs uppercase tracking-[0.18em] text-zinc-500">{t.where}</p>
                  </figcaption>
                </figure>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* ————— Client portal band ————— */}
      <section data-testid="portal-band" className="bg-pine py-20 text-paper lg:py-24">
        <div className="mx-auto flex max-w-7xl flex-col items-start gap-8 px-6 sm:flex-row sm:items-center sm:justify-between lg:px-10">
          <FadeUp>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-paper/60">
              Existing clients
            </p>
            <h2 className="mt-4 font-serif text-3xl tracking-tight sm:text-4xl">
              Already working with us?
            </h2>
            <p className="mt-3 max-w-md text-sm leading-relaxed text-paper/70">
              Share documents, review deliverables, and message our team through your secure
              client portal.
            </p>
          </FadeUp>
          <FadeUp delay={0.15}>
            <Link
              to="/portal"
              data-testid="portal-band-button"
              className="group inline-flex items-center gap-2 rounded-full bg-white px-7 py-4 text-sm font-semibold text-pine transition-colors duration-300 hover:bg-clay hover:text-white"
            >
              Go to client portal
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </Link>
          </FadeUp>
        </div>
      </section>

      {/* ————— FAQ ————— */}
      <Faq />

      {/* ————— Final CTA ————— */}
      <section data-testid="final-cta" className="py-28 lg:py-36">
        <div className="mx-auto max-w-4xl px-6 text-left lg:px-10">
          <FadeUp>
            <p className="flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.25em] text-pine">
              <span className="h-px w-10 bg-pine" />
              Start here
            </p>
            <h2 className="mt-8 font-serif text-4xl leading-[1.1] tracking-tighter text-ink sm:text-5xl lg:text-6xl">
              Let&rsquo;s get your books <em className="text-pine">audit-ready.</em>
            </h2>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-zinc-600 sm:text-lg">
              Tell us where your finances stand today. We will respond within one business
              day with a clear next step — no pressure, no jargon.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/contact"
                data-testid="final-cta-contact-button"
                className="group inline-flex items-center gap-2 rounded-full bg-pine px-7 py-4 text-sm font-semibold text-white transition-colors duration-300 hover:bg-clay"
              >
                Request a consultation
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <Link
                to="/contact#booking"
                data-testid="final-cta-booking-button"
                className="group inline-flex items-center gap-2 rounded-full border border-black/15 px-7 py-4 text-sm font-semibold text-ink transition-colors duration-300 hover:border-pine hover:text-pine"
              >
                Book an appointment
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </Link>
            </div>
          </FadeUp>
        </div>
      </section>
    </>
  );
}
